import React, { createContext, useState, useEffect, useContext } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Switch, Platform, ScrollView } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ShieldCheck, X } from 'lucide-react-native';
// Importiamo il context dei vini per agganciare la lingua reattiva del profilo
import { useWineContext } from '../context/WineContext';

// 1. CREAZIONE DEL CONTESTO GLOBALE PER IL CONSENSO
export const ConsentContext = createContext({});
export const useConsent = () => useContext(ConsentContext);

export const ConsentProvider = ({ children }) => {
  // Estraiamo il profilo e il motore di traduzione centralizzato
  const { t, userProfile } = useWineContext();

  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Stati temporanei per gli switch del Modal
  const [tempAnalytics, setTempAnalytics] = useState(false);
  const [tempMarketing, setTempMarketing] = useState(false);

  // AL CARICAMENTO: Controlla se esiste già una scelta salvata
  useEffect(() => {
    const loadConsent = async () => {
      try {
        const saved = await AsyncStorage.getItem('@vinologic_consent');
        if (saved) {
          const parsedChoices = JSON.parse(saved);
          setConsent(parsedChoices);
          executePriorBlocking(parsedChoices); // Applica le regole GDPR
        } else {
          // Primo accesso: Mostra il banner
          setShowBanner(true);
        }
      } catch (error) {
        console.error("Errore caricamento cookie consent:", error);
      }
    };
    loadConsent();
  }, []);

  // 2. IL "PRIOR BLOCKING" (GDPR COMPLIANT)
  const executePriorBlocking = (choices) => {
    if (choices.analytics) {
      console.log("[GDPR] ✅ Analytics APPROVATI: Inizializzazione Firebase Analytics...");
    } else {
      console.log("[GDPR] ❌ Analytics RIFIUTATI: Nessun tracciamento statistico.");
    }

    if (choices.marketing) {
      console.log("[GDPR] ✅ Marketing APPROVATI: Inizializzazione AdMob/Pixel...");
    } else {
      console.log("[GDPR] ❌ Marketing RIFIUTATI: Nessun tracciamento pubblicitario.");
    }
  };

  // 3. SALVATAGGIO SCELTE (PERSISTENZA)
  const saveChoices = async (choices) => {
    try {
      await AsyncStorage.setItem('@vinologic_consent', JSON.stringify(choices));
      setConsent(choices);
      executePriorBlocking(choices);
      setShowBanner(false);
      setShowPreferences(false);
    } catch (error) {
      console.error("Errore salvataggio scelte cookie:", error);
    }
  };

  // Azioni Rapide dal Banner
  const acceptAll = () => saveChoices({ technical: true, analytics: true, marketing: true });
  const rejectAll = () => saveChoices({ technical: true, analytics: false, marketing: false });
  
  // Apertura/Chiusura Modal Personalizzazione
  const openPreferences = () => {
    setTempAnalytics(consent?.analytics || false);
    setTempMarketing(consent?.marketing || false);
    setShowPreferences(true);
  };
  const saveCustomPreferences = () => saveChoices({ technical: true, analytics: tempAnalytics, marketing: tempMarketing });

  // PARACADUTE ANTI-CRASH: Se il profilo utente (e la sua lingua) non è ancora stato caricato 
  // dal WineProvider, attendiamo la fine del caricamento asincrono per evitare stringhe errate o flash grafici.
  if (!userProfile) return children;

  return (
    <ConsentContext.Provider value={{ consent, openPreferences }}>
      {children}

      {/* BANNER INIZIALE (Tradotto reattivamente tramite context) */}
      <Modal transparent={true} visible={showBanner} animationType="slide">
        <View style={styles.bannerOverlay}>
          <View style={styles.bannerBox}>
            <View style={styles.bannerHeader}>
              <ShieldCheck color="#0f172a" size={28} />
              <Text style={styles.bannerTitle}>{t('components.cookieConsent.title')}</Text>
            </View>
            <Text style={styles.bannerText}>
              {t('components.cookieConsent.desc')}
            </Text>
            
            <View style={styles.bannerButtons}>
              <TouchableOpacity style={styles.neutralButton} onPress={acceptAll}>
                <Text style={styles.neutralButtonText}>{t('components.cookieConsent.acceptAll')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.neutralButton} onPress={rejectAll}>
                <Text style={styles.neutralButtonText}>{t('components.cookieConsent.rejectAll')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.neutralButton} onPress={openPreferences}>
                <Text style={styles.neutralButtonText}>{t('components.cookieConsent.preferences')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* MODAL PERSONALIZZAZIONE (Granularità GDPR) */}
      <Modal transparent={true} visible={showPreferences} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHeaderRow}>
              <Text style={styles.modalTitle}>{t('components.cookieConsent.titlePref')}</Text>
              {consent && (
                <TouchableOpacity onPress={() => setShowPreferences(false)}>
                  <X color="#64748b" size={24} />
                </TouchableOpacity>
              )}
            </View>

            <ScrollView style={styles.switchesContainer}>
              {/* COOKIE TECNICI (Bloccati su ON) */}
              <View style={styles.switchRow}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchTitle}>{t('components.cookieConsent.essentialTitle')}</Text>
                  <Text style={styles.switchDesc}>{t('components.cookieConsent.essentialDesc')}</Text>
                </View>
                <Switch value={true} disabled={true} trackColor={{ true: '#cbd5e1' }} thumbColor="#94a3b8" />
              </View>

              {/* COOKIE STATISTICI */}
              <View style={styles.switchRow}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchTitle}>{t('components.cookieConsent.analyticsTitle')}</Text>
                  <Text style={styles.switchDesc}>{t('components.cookieConsent.analyticsDesc')}</Text>
                </View>
                <Switch 
                  value={tempAnalytics} 
                  onValueChange={setTempAnalytics} 
                  trackColor={{ false: '#e2e8f0', true: '#fecdd3' }} 
                  thumbColor={tempAnalytics ? '#e11d48' : '#f8fafc'} 
                />
              </View>

              {/* COOKIE MARKETING */}
              <View style={styles.switchRow}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchTitle}>{t('components.cookieConsent.marketingTitle')}</Text>
                  <Text style={styles.switchDesc}>{t('components.cookieConsent.marketingDesc')}</Text>
                </View>
                <Switch 
                  value={tempMarketing} 
                  onValueChange={setTempMarketing} 
                  trackColor={{ false: '#e2e8f0', true: '#fecdd3' }} 
                  thumbColor={tempMarketing ? '#e11d48' : '#f8fafc'} 
                />
              </View>
            </ScrollView>

            <TouchableOpacity style={styles.savePrefButton} onPress={saveCustomPreferences}>
              <Text style={styles.savePrefText}>{t('components.cookieConsent.savePreferences')}</Text>
            </TouchableOpacity>

          </View>
        </View>
      </Modal>

    </ConsentContext.Provider>
  );
};

const styles = StyleSheet.create({
  bannerOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.4)', justifyContent: 'flex-end' },
  bannerBox: { backgroundColor: 'white', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: Platform.OS === 'ios' ? 40 : 24, boxShadow: '0px -5px 20px rgba(0,0,0,0.1)', elevation: 10 },
  bannerHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  bannerTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 10 },
  bannerText: { fontSize: 14, color: '#475569', lineHeight: 20, marginBottom: 20 },
  bannerButtons: { flexDirection: 'column', gap: 10 },
  neutralButton: { backgroundColor: '#f1f5f9', paddingVertical: 14, borderRadius: 12, alignItems: 'center', borderWidth: 1, borderColor: '#e2e8f0' },
  neutralButtonText: { color: '#0f172a', fontWeight: 'bold', fontSize: 16 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.7)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalBox: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 450, maxHeight: '80%', boxShadow: '0px 10px 25px rgba(0,0,0,0.2)', elevation: 10 },
  modalHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  modalTitle: { fontSize: 22, fontWeight: 'bold', color: '#0f172a' },
  switchesContainer: { marginBottom: 20 },
  switchRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  switchInfo: { flex: 1, paddingRight: 16 },
  switchTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 4 },
  switchDesc: { fontSize: 13, color: '#64748b', lineHeight: 18 },
  savePrefButton: { backgroundColor: '#e11d48', paddingVertical: 16, borderRadius: 12, alignItems: 'center' },
  savePrefText: { color: 'white', fontWeight: 'bold', fontSize: 16 }
});